import React, { useEffect, useState } from "react";
import { LifeBuoy, Save, PhoneCall, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { BannerMessageSettings } from "../components/BannerMessage";
import { FirstTimerMessagesSettings } from "../components/FirstTimerMessages";
import { GivingSwitch } from "../components/GivingSwitch";
import { IncomingTextsCard } from "../components/IncomingTextsCard";
import api, { formatApiError } from "../lib/api";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const INPUT = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-[#0B5E4E]";

const CALL_APPS = [
  { v: "menu", label: "Ask each time (show the menu)" },
  { v: "webex", label: "Webex" },
  { v: "dialer", label: "Phone dialer" },
  { v: "whatsapp", label: "WhatsApp" },
  { v: "inapp", label: "Call through the app (rings staff, then connects to member)" },
];

const hourLabel = (h) => {
  const ampm = h < 12 ? "am" : "pm";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

export default function PortalSettings() {
  const [form, setForm] = useState({ phone: "", email: "", hours: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [callApp, setCallApp] = useState("inapp");
  const [savingCall, setSavingCall] = useState(false);
  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [reminderDay, setReminderDay] = useState(0);
  const [reminderHour, setReminderHour] = useState(9);
  const [savingDay, setSavingDay] = useState(false);

  useEffect(() => { api.get("/settings/reminder-day").then(({ data }) => { setReminderDay(data.day ?? 0); setReminderHour(data.hour ?? 9); }).catch(() => {}); }, []);
  const saveReminderDay = async () => {
    setSavingDay(true);
    try { await api.put("/settings/reminder-day", { day: reminderDay, hour: reminderHour }); toast.success(`Weekly reminder will be sent every ${WEEKDAYS[reminderDay]} at ${hourLabel(reminderHour)}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSavingDay(false); }
  };

  useEffect(() => { api.get("/support-contact").then(({ data }) => setForm({ phone: data.phone || "", email: data.email || "", hours: data.hours || "" })).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { api.get("/call-app-default").then(({ data }) => setCallApp(data.default_call_app || "inapp")).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/support-contact", form); toast.success("Members Portal Support details saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const saveCallApp = async () => {
    setSavingCall(true);
    try {
      await api.put("/call-app-default", { default_call_app: callApp });
      toast.success("Default calling app saved for the whole team");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSavingCall(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-settings-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold flex items-center gap-3" style={{ color: "var(--sj-text)" }}>
          <LifeBuoy className="h-9 w-9" style={{ color: "var(--sj-primary)" }} /> Portal Settings
        </h1>
        <p className="text-base mt-2" style={{ color: "var(--sj-text-2)" }}>Set the <b>Members Portal Support</b> contact shown on the login page to help members who can't sign in.</p>
      </div>

      <div className={`${CARD} p-6 space-y-4`}>
        {loading ? <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Loading…</p> : (
          <>
            <div>
              <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Support phone number</label>
              <input data-testid="support-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 020 1234 5678" className={`${INPUT} mt-1`} />
            </div>
            <div>
              <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Support email address</label>
              <input data-testid="support-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="e.g. office@streamsofjoyerith.org" className={`${INPUT} mt-1`} />
            </div>
            <div>
              <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Office hours <span style={{ color: "var(--sj-text-3)" }}>(optional)</span></label>
              <input data-testid="support-hours-input" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="e.g. Mon–Fri, 9am–5pm" className={`${INPUT} mt-1`} />
            </div>
            <button data-testid="save-support-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save details"}
            </button>
          </>
        )}
      </div>

      <GivingSwitch />
      <IncomingTextsCard />

      <BannerMessageSettings />

      <FirstTimerMessagesSettings />

      <div className={`${CARD} p-6 space-y-4`} data-testid="call-app-card">
        <div>
          <h2 className="font-head text-xl font-semibold flex items-center gap-2" style={{ color: "var(--sj-text)" }}>
            <PhoneCall className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Default calling app
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>Choose which app opens when staff tap a member's phone number. This applies to everyone who hasn't set their own preference. Your organisation uses <b>Call through the app</b>.</p>
        </div>
        <div>
          <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>When a phone number is tapped, use</label>
          <select data-testid="org-call-app-select" value={callApp} onChange={(e) => setCallApp(e.target.value)} className={`${INPUT} mt-1`}>
            {CALL_APPS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
          </select>
        </div>
        <button data-testid="save-call-app-btn" onClick={saveCallApp} disabled={savingCall} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
          <Save className="h-4 w-4" /> {savingCall ? "Saving…" : "Save default"}
        </button>
      </div>

      <div className={`${CARD} p-6 space-y-4`} data-testid="reminder-day-card">
        <div>
          <h2 className="font-head text-xl font-semibold flex items-center gap-2" style={{ color: "var(--sj-text)" }}>
            <CalendarClock className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Weekly service reminder
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>Members who opt in get one reminder each week listing all upcoming services, with a link to their portal. Choose which day and time it's sent (London time).</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Send the weekly reminder every</label>
            <select data-testid="reminder-day-select" value={reminderDay} onChange={(e) => setReminderDay(Number(e.target.value))} className={`${INPUT} mt-1`}>
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>At (London time)</label>
            <select data-testid="reminder-hour-select" value={reminderHour} onChange={(e) => setReminderHour(Number(e.target.value))} className={`${INPUT} mt-1`}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </div>
        </div>
        <button data-testid="save-reminder-day-btn" onClick={saveReminderDay} disabled={savingDay} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
          <Save className="h-4 w-4" /> {savingDay ? "Saving…" : "Save reminder schedule"}
        </button>
      </div>
    </div>
  );
}
